/**
 * End of Life Grid
 * List view for End of Life records — story 37057.
 * Structural base: packaging-pcf/material-emission-factors/material-emission-grid.
 */

import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Input, Breadcrumbs, Button } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { GET_ALL_END_OF_LIFE } from '../../../query/end-of-life'
import { EXPORT_END_OF_LIFE_QUERY } from '../../../query/export-end-of-life'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'
import { EXPORT_MAX_ROW_COUNT, ROUTES } from '../../../const'
import { IEndOfLifeGridItem } from './types'
import { getEndOfLifeVars } from './helpers'

// @ts-ignore - SCSS module import
import './index.scss'

// A record is a country + taxonomy level + product combination, so a country appears on several
// rows. Sorting by country then level groups each country's category row above its functional-unit
// rows instead of interleaving them.
const DEFAULT_ORDER = [{ countryName: 'ASC' }, { productTaxonomyLevel: 'ASC' }]

const EndOfLifeGrid: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IEndOfLifeGridItem[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [endOfLifeVars, setEndOfLifeVars] = useState<any>(getEndOfLifeVars)
  const [order, setOrder] = useState<any>(DEFAULT_ORDER)

  const { data, loading, refetch } = useQuery(GET_ALL_END_OF_LIFE, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getEndOfLifeVars,
    },
  })

  const anyRefetch = useCallback(
    (variables?: any): Promise<any> => {
      return refetch(variables)
    },
    [refetch]
  )

  const setCursors = useCallback(() => {
    if (data?.endOfLife?.pageInfo) {
      setEndCursor(data.endOfLife.pageInfo.endCursor)
      setStartCursor(data.endOfLife.pageInfo.startCursor)
    }
  }, [data])

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    // Refetch on mount/data-change so navigating back to the grid shows fresh data instead of a
    // stale Apollo cache-first result (repo convention, see CLAUDE.md "Grid remount refetch").
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      ROUTES.endOfLifeItem,
      'endOfLife',
      'endOfLifeRecord'
    )
  }, [data, loading, refetch, setCursors])

  useEffect(() => {
    if (tableSearch != null || !order[0].countryName) {
      if (!loading) {
        setEndOfLifeVars({
          ...filterEnums(tableSearch, getEndOfLifeVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getEndOfLifeVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading, data, anyRefetch])

  // operationName must equal the root query field name exactly — the export endpoint resolves it
  // against the schema's Query type and 500s otherwise (bugs 37321, 39141).
  const exportPayload = useMemo(
    () => ({
      operationName: 'EndOfLife',
      query: EXPORT_END_OF_LIFE_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  const handleAddNew = useCallback(() => {
    navigate(`${ROUTES.endOfLifeItem}?itemId=new`)
  }, [navigate])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDebouncedTableSearch(e.target.value)
  }, [])

  return (
    <div className={'end-of-life'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[
              {
                path: ROUTES.endOfLife,
                title: 'End of Life Table',
              },
            ]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>End of Life</h2>
        <div className="packaging-actions items-center">
          <Input onChange={handleSearchChange} iconType="search" placeholder="Search" />
          <Button variant="primary" onClick={handleAddNew}>
            Add End of Life
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={endOfLifeVars || getEndOfLifeVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default EndOfLifeGrid
